<template>
  <div>
    <v-header></v-header>
    <div class="container fix-date-change my-4">
        <h1 class="h3 mb-4">Cambio de fecha de Ticket Fix</h1>

        <!-- Paso 1: Búsqueda de la orden -->
        <div v-if="step === 'search'" class="card p-4">
            <p class="text-muted">
                Ingresa el correo del comprador y el número de orden para buscar tus Tickets Fix.
            </p>
            <form @submit.prevent="search">
                <div class="form-group">
                    <label for="fdc-email">Correo del comprador</label>
                    <input id="fdc-email" type="email" class="form-control" v-model.trim="email" required>
                </div>
                <div class="form-group">
                    <label for="fdc-order">Número de orden</label>
                    <input id="fdc-order" type="number" class="form-control" v-model.trim="orderId" required>
                </div>
                <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
                <button class="btn btn-primary" :disabled="loading">
                    <span v-if="loading">Buscando…</span>
                    <span v-else>Buscar orden</span>
                </button>
            </form>
        </div>

        <!-- Términos y Condiciones (solo en el paso de búsqueda) -->
        <div v-if="step === 'search'" class="card p-4 mt-4">
            <h2 class="h5 mb-3">Términos y Condiciones – Cambio de Fecha Ticket FIX</h2>
            <ol class="text-muted small mb-0 pl-3">
                <li class="mb-2">
                    <strong>Cambios de fecha:</strong> Los Tickets FIX podrán ser modificados únicamente hasta 48 horas antes de la fecha originalmente adquirida para su uso. Una vez vencido este plazo, no será posible realizar cambios de fecha.
                </li>
                <li class="mb-2">
                    <strong>Disponibilidad:</strong> Toda solicitud de cambio de fecha estará sujeta a la disponibilidad de cupos y productos para la nueva fecha solicitada. Valle Nevado no garantiza disponibilidad para todas las fechas de la temporada.
                </li>
                <li class="mb-2">
                    <strong>Diferencia tarifaria:</strong> Si el valor vigente del Ticket FIX para la nueva fecha es superior al monto originalmente pagado, el cliente deberá pagar la diferencia tarifaria correspondiente antes de que el cambio sea procesado. Si la nueva fecha tiene un valor inferior, no procederá devolución, reembolso, abono o compensación por la diferencia de precio.
                </li>
                <li class="mb-2">
                    <strong>Nueva Orden de Compra:</strong> Una vez confirmado el cambio, Valle Nevado emitirá una nueva Orden de Compra asociada a la fecha seleccionada, la que será enviada al correo electrónico registrado por el cliente al momento de la compra. Será de exclusiva responsabilidad del cliente revisar dicho correo y verificar la correcta recepción de la nueva documentación.
                </li>
                <li class="mb-2">
                    <strong>Invalidación del ticket original:</strong> Una vez emitida la nueva Orden de Compra, el Ticket FIX y la Orden de Compra originalmente emitidos quedarán automáticamente anulados e inválidos, por lo que no podrán ser utilizados para ingresar al centro de ski ni para ningún otro efecto.
                </li>
                <li class="mb-0">
                    De conformidad con lo dispuesto en el artículo 3 bis, letra b), de la Ley N.º 19.496 sobre Protección de los Derechos de los Consumidores, el derecho a retracto no resulta aplicable a la compra de Tickets FIX de Valle Nevado, por tratarse de un servicio asociado a una fecha determinada para su prestación. En consecuencia, no se efectuarán devoluciones, reembolsos, anulaciones ni cambios distintos a los expresamente regulados en estas condiciones, salvo en aquellos casos en que la ley o Valle Nevado dispongan expresamente lo contrario.
                </li>
            </ol>
        </div>

        <!-- Paso 2: Selección del ticket -->
        <div v-else-if="step === 'select'" class="card p-4">
            <p>Orden <strong>#{{ orderId }}</strong> — Selecciona el ticket que deseas cambiar:</p>
            <ul class="list-group mb-3">
                <li v-for="ticket in tickets" :key="ticket.detail_uuid"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'list-group-item-light text-muted': !ticket.eligible }">
                    <div>
                        <div class="font-weight-bold">{{ ticket.purchase_detail }}</div>
                        <small>Fecha de uso: {{ formatDate(ticket.start_date) }}</small>
                        <div v-if="!ticket.eligible" class="text-danger small mt-1">{{ ticket.reason }}</div>
                    </div>
                    <button class="btn btn-sm btn-outline-primary"
                            :disabled="!ticket.eligible"
                            @click="selectTicket(ticket)">
                        Cambiar fecha
                    </button>
                </li>
            </ul>
            <button class="btn btn-link px-0" @click="reset">← Buscar otra orden</button>
        </div>

        <!-- Paso 3: Nueva fecha + cotización -->
        <div v-else-if="step === 'quote'" class="card p-4">
            <p>
                Ticket: <strong>{{ selected.purchase_detail }}</strong><br>
                Fecha actual: <strong>{{ formatDate(selected.start_date) }}</strong>
            </p>
            <div class="form-group">
                <label for="fdc-new-date">Nueva fecha de uso</label>
                <input id="fdc-new-date" type="date" class="form-control" v-model="newDate"
                       :min="minNewDate" @change="getQuote">
            </div>

            <div v-if="quoteLoading" class="text-muted">Calculando…</div>
            <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

            <div v-if="quote && !error" class="abono-box border rounded p-3 mb-3">
                <div class="d-flex justify-content-between">
                    <span>Precio pagado</span><span>{{ money(quote.old_price) }}</span>
                </div>
                <div v-if="quote.new_price >= quote.old_price" class="d-flex justify-content-between">
                    <span>Precio nueva fecha</span><span>{{ money(quote.new_price) }}</span>
                </div>
                <hr>
                <div class="d-flex justify-content-between font-weight-bold">
                    <span>Abono a pagar</span>
                    <span>{{ quote.difference > 0 ? money(quote.difference) : 'Sin costo adicional' }}</span>
                </div>
                <div v-if="quote.difference < 0" class="alert alert-warning small mb-0 mt-2">
                    No procederá devolución, reembolso, abono o compensación por la diferencia de precio.
                </div>
                <p v-if="quote.card_price > 0" class="small text-muted mb-0 mt-2">
                    Ambos precios incluyen {{ money(quote.card_price) }} de tarjeta Valle Plus.
                    El abono corresponde solo a la diferencia del ticket.
                </p>
            </div>

            <div class="d-flex justify-content-between">
                <button class="btn btn-link px-0" @click="step = 'select'">← Volver</button>
                <button class="btn btn-primary"
                        :disabled="!quote || confirmLoading"
                        @click="confirm">
                    <span v-if="confirmLoading">Procesando…</span>
                    <span v-else-if="quote && quote.difference > 0">Pagar abono y confirmar</span>
                    <span v-else>Confirmar cambio</span>
                </button>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import numeral from 'numeral';
import API from '../../api/index';

export default {
    data() {
        return {
            step: 'search',
            email: '',
            orderId: '',
            tickets: [],
            selected: null,
            newDate: '',
            quote: null,
            error: '',
            loading: false,
            quoteLoading: false,
            confirmLoading: false,
        };
    },

    computed: {
        // La nueva fecha debe ser, como mínimo, mañana (la validación real de
        // la ventana de cambio la hace el backend).
        minNewDate() {
            return moment().add(1, 'days').format('YYYY-MM-DD');
        },
    },

    methods: {
        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '';
        },

        money(value) {
            return numeral(value).format('$0,0');
        },

        errorFrom(e) {
            const data = e && e.response && e.response.data;
            if (!data) return 'Ocurrió un error. Intenta nuevamente.';
            if (typeof data.detail === 'string') return data.detail;
            if (data.new_date) return Array.isArray(data.new_date) ? data.new_date[0] : data.new_date;
            const firstKey = Object.keys(data)[0];
            const val = firstKey ? data[firstKey] : null;
            return Array.isArray(val) ? val[0] : (val || 'Ocurrió un error.');
        },

        search() {
            this.error = '';
            this.loading = true;
            API.fix_date_change.search({ email: this.email, orderId: this.orderId })
                .then((data) => {
                    this.tickets = data.tickets;
                    this.step = 'select';
                })
                .catch((e) => { this.error = this.errorFrom(e); })
                .finally(() => { this.loading = false; });
        },

        selectTicket(ticket) {
            this.selected = ticket;
            this.newDate = '';
            this.quote = null;
            this.error = '';
            this.step = 'quote';
        },

        getQuote() {
            if (!this.newDate) return;
            this.error = '';
            this.quote = null;
            this.quoteLoading = true;
            API.fix_date_change.quote({
                email: this.email,
                orderId: this.orderId,
                detailUuid: this.selected.detail_uuid,
                newDate: this.newDate,
            })
                .then((data) => { this.quote = data; })
                .catch((e) => { this.error = this.errorFrom(e); })
                .finally(() => { this.quoteLoading = false; });
        },

        confirm() {
            this.error = '';
            this.confirmLoading = true;
            API.fix_date_change.confirm({
                email: this.email,
                orderId: this.orderId,
                detailUuid: this.selected.detail_uuid,
                newDate: this.newDate,
            })
                .then((data) => {
                    // El flujo de pago/confirmación es server-rendered: navegamos.
                    window.location.href = data.redirect_url;
                })
                .catch((e) => {
                    this.error = this.errorFrom(e);
                    this.confirmLoading = false;
                });
        },

        reset() {
            this.step = 'search';
            this.tickets = [];
            this.selected = null;
            this.quote = null;
            this.error = '';
        },
    },
};
</script>

<style scoped>
.fix-date-change {
    max-width: 640px;
}
.abono-box {
    background: #f7f9fc;
}
</style>



// WEBPACK FOOTER //
// src/components/Pages/FixDateChangePage.vue